/**
 * Explorer links and their names. The base URL is the network's own, as the
 * session names it (`GET /maker-api/auth/me`, `chains[].explorerUrl`); the
 * name is that of the `/chains.json` network whose explorer has the link's
 * host, else the host itself, so a name always describes the explorer linked.
 */

import type { MakerChain } from "./api";

/** Host → name, from every `/chains.json` read (`loadChains`). */
const names = new Map<string, string>();

function hostOf(url: string): string | null {
  try {
    const { host } = new URL(url);
    return host === "" ? null : host;
  } catch {
    return null;
  }
}

/** Records each listed network's explorer name under its host; the first network naming a host wins. */
export function nameExplorers(chains: readonly { explorer?: { name: string; url: string } | null }[]): void {
  names.clear();
  const seen = new Set<string>();
  for (const { explorer } of chains) {
    const host = explorer ? hostOf(explorer.url) : null;
    if (explorer && host !== null && !seen.has(host)) {
      seen.add(host);
      names.set(host, explorer.name);
    }
  }
}

/** Tests only: forget every recorded name. */
export function resetExplorerNames(): void {
  names.clear();
}

/** The listed network's explorer name ("BscScan"), else the link's host; "Explorer" when it is not a URL. */
export function explorerName(url: string | null | undefined): string {
  const host = url === null || url === undefined ? null : hostOf(url);
  return host === null ? "Explorer" : (names.get(host) ?? host);
}

function base(chain: MakerChain): string | null {
  return chain.explorerUrl === null || chain.explorerUrl === "" ? null : chain.explorerUrl.replace(/\/+$/, "");
}

export function addressUrl(chain: MakerChain, address: string): string | null {
  const b = base(chain);
  return b === null ? null : `${b}/address/${address}`;
}

export function txUrl(chain: MakerChain, hash: string): string | null {
  const b = base(chain);
  return b === null ? null : `${b}/tx/${hash}`;
}
